import { useEffect, useState } from 'react';
import { Descriptions, Divider, Flex, Space } from 'antd';
import { DataGrid, DeleteButton, EditButton, ListButton, NewTag, ExcelButton } from 'components/elements';
import { BaseContainer, ConfirmModal, SimpleModal } from 'components/containers';
import { IconDown, IconPaperClip, IconUp } from 'components/assets/icons';
import { DownloadExcel, Attachment } from 'pages/common';
import dayjs from 'dayjs';
import { deleteBoardTaskAPI, getBoardTaskDetailAPI, getBoardTaskReadMemberAPI } from 'api/board';
import { getFormatDate, objToLowerCase } from 'utils/utils';
import { boardTaskTypeOptions } from 'data/options';
import { useAuth } from 'hooks/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { URL_ADMIN } from 'routes/AdminRoute';
import { URL_BOARD_TASK, URL_BOARD_TASK_DETAIL, URL_BOARD_TASK_EDIT } from 'routes/CommonRoute';
import { BaseSection } from 'components/layouts';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Viewer } from 'components/elements/Viewer';

const BoardTaskDetail = () => {
  const navigate = useNavigate();
  const { no } = useParams();
  const { user } = useAuth();

  const [value, setValue] = useState(null);
  const [open, setOpen] = useState(true);
  const [readModal, setReadModal] = useState({open: false, list: []})
  const [deleteModal, setDeleteModal] = useState({open: false})

  const fnGetDetail = async () => {
    try {
      const { data } = await getBoardTaskDetailAPI(no)
      const { data_info, read_info } = data
      setValue({ ...objToLowerCase(data_info), ...objToLowerCase(read_info) })
    } catch (err) {
      console.error(err)
    }
  }

  const fnDelete = async () => {
    try {
      await deleteBoardTaskAPI([no])
      navigate(`${URL_ADMIN}${URL_BOARD_TASK}`)
    } catch (err) {
      console.error(err)
    }
  };

  const fnGetReadList = async () => {
    if (readModal.list.length !== 0) {
      setReadModal({...readModal, open: true })
    } else {
      try {
        const { data } = await getBoardTaskReadMemberAPI(no)
        const { data_list } = data
        setReadModal({...readModal, open: true, list: data_list})
      } catch (err) {
        console.error(err)
      }
    }
  }

  
  useEffect(() => {
    if (no) fnGetDetail();
  }, [no]);

  const titleColumns = [
    {
      key: '1',
      title: (
        <>
          {value?.title}
          {dayjs(value?.regist_date)?.isSame(dayjs(), 'day') && <NewTag />}
        </>
      ),
      dataIndex: 'board_type',
      align: 'center',
      width: '20%',
      colSpan: 5,
      render: (text) => <Space>구분 : {boardTaskTypeOptions?.find((it) => it?.value === text)?.label}</Space>
    },
    { key: '2', title: '', dataIndex: 'writer', align: 'center', width: '20%', colSpan: 0, render: (text, record) => <Space>작성자 : {text}{`(${record?.team_name})`}</Space> },
    { key: '3', title: '', dataIndex: 'regist_date', align: 'center', width: '20%', colSpan: 0, render: (text) => <Space>등록일 : {getFormatDate(text)}</Space> },
    { key: '4', title: '', dataIndex: 'idx', align: 'center', width: '20%', colSpan: 0, render: (text) => <Space>문서번호 : {text}</Space> },
    { key: '5', title: '', dataIndex: 'hits', align: 'center', width: '20%', colSpan: 0, render: (text) => <Space>조회수 : {text}</Space> }
  ];

  const fileColumns = [
    {
      key: '1',
      title: '',
      dataIndex: 'attached_file',
      render: (text, record) => (
        <>
          {open ? <IconUp onClick={() => setOpen(false)} /> : <IconDown onClick={() => setOpen(true)} />} 첨부
          {open && (
            <div className="attachment-container">
              <Space direction={'vertical'} size={1}>
                {record?.file_list?.ATTACHED_FILE_PATH?.map((item, index) => (
                  <Attachment key={index} label={`${record?.file_list?.ATTACHED_FILE?.[index]}`} icon={<IconPaperClip />} url={item} maxLength={100} />
                ))}
              </Space>
            </div>
          )}
        </>
      )
    }
  ];

  const readColumns = [
    {
      key: '1',
      title: '',
      render: (_, record) => (
        <Space>
          열람 :
          <div className="text-click" onClick={fnGetReadList}>
            <span className="text-blue">{record?.read_cnt}명 </span>/ {record?.total_cnt}명 
          </div>
        </Space>
      )
    }
  ];

  const navigateItems = [
    {
      key: '1',
      label: '이전글',
      children: 
        value?.prev 
          ? 
            <span className="text-click" onClick={() => navigate(`${URL_ADMIN}${URL_BOARD_TASK_DETAIL.replace(':no', value?.prev?.IDX)}`)}>
              {value?.prev?.TITLE}
            </span>
          : '-'
    },
    {
      key: '2',
      label: '다음글',
      children: 
        value?.next 
          ? 
            <span className="text-click" onClick={() => navigate(`${URL_ADMIN}${URL_BOARD_TASK_DETAIL.replace(':no', value?.next?.IDX)}`)}>
              {value?.next?.TITLE}
            </span>
          : '-'
    }
  ];

  const modalColumns = [
    { title: '구분', align: 'center', width: '20%', dataIndex: 'USER_LEVEL'},
    { title: '회원명', align: 'center', width: '40%', dataIndex: 'READ_NAME' },
    { title: '열람일', align: 'center', width: '40%', dataIndex: 'CREATE_DATE' }
  ];

  return (
    <BaseSection>
      <BaseContainer>
        <DataGrid columns={titleColumns} data={[value]} />
        {value?.attached_file?.length && <DataGrid columns={fileColumns} data={[value]} showHeader={false} bordered={false} />}
        <DataGrid columns={readColumns} data={[value]} showHeader={false} bordered={false} />
        <Divider className="divider-small" />

        <Viewer data={value?.content} />
        <Divider className="divider" />

        <Descriptions items={navigateItems} column={1} bordered labelStyle={{ width: '10%', textAlign: 'center' }} contentStyle={{ textAlign: 'left' }} />
        <Divider className="divider" />

        <Flex justify={'end'} gap={3}>
          {user.user_idx === value?.writer_idx && (
            <>
              <DeleteButton onClick={() => setDeleteModal({...deleteModal, open: true})} />
              <EditButton onClick={() => navigate(`${URL_ADMIN}${URL_BOARD_TASK_EDIT.replace(':no', no)}`)} />
            </>
          )}

          <ListButton onClick={() => navigate(`${URL_ADMIN}${URL_BOARD_TASK}`)} />
        </Flex>
      </BaseContainer>

    {/* 열람회원 모달 */}
      <SimpleModal open={readModal?.open} width={'600px'} title={'열람 회원 목록'} onClose={() => setReadModal({...readModal, open: false})}>
        <Flex justify={'space-between'} align={'center'}>
          <span>열람 회원 수: {value?.read_cnt} 명</span>
          <ExcelButton
              // label={'엑셀 다운로드(검색)'} //뷰네임 미입력시 기본값
              // isCheckComp={true} //체크박스 컴포넌트 여부 미입력시 X
              headerList={modalColumns} //필수 헤더리스트 표 설정과 동일한 변수여야함
              gridList={readModal?.list} //필수 실시간 그리드 데이터
              // filterList={[]} //뺄 칼럼 목록 dataIndex 미입력시 dataIndex 키 없는것만 뺌
              // renderList={[]} //코드 or 다른이름으로 바꿔서 다운할 목록
              // checkRows={} //isCheckComp true 일시 checkRows 데이터 미입력시 체크작동안함
              // checkKey={} //isCheckComp true 일시 check 한 데이터 dataIndex 키 네임 미입력시 체크작동안함
            />
        </Flex>
        <DataGrid data={readModal?.list} columns={modalColumns} pagination={false} />
      </SimpleModal>

      <ConfirmModal
        open={deleteModal?.open}
        onConfirm={fnDelete}
        onCancel={() => setDeleteModal({...deleteModal, open: false})}
      >
        <Flex vertical={true} justify={'center'} align={'center'} gap={'large'}>
          <InfoCircleOutlined style={{color: 'orange', fontSize: '40px'}} />
          삭제 하시겠습니까?
        </Flex>
      </ConfirmModal>
    </BaseSection>
  );
};

export default BoardTaskDetail;
